import { useMemo } from 'react';
import _ from 'lodash';
import { Button, ButtonGroup, FormFeedback } from 'reactstrap';
import { FieldArray, useFormikContext } from 'formik';
import { gql } from '@apollo/client';
import { nanoid } from 'nanoid';
import { useParams } from 'react-router-dom';
import { useDataContext } from '../../../contexts/DataContext';
import { ResourceSelectionFormGroup } from '../template-components';
import { useQueryWithFarmId } from '../../../utils/hooks';
import { SelectorFormGroup, SquarePlusButton } from './SelectorElements';
import { optionsAreEqualById } from '../../basics/StandardTypeahead';

const capexFields = [
  'id',
  'name',
  'requiresFuel',
  'gallonsFuelPerHour',
  'isImplement',
];

const TemplateCapexOptionsQuery = gql`
  query TemplateCapexOptions {
    capexResources {
      ${capexFields.join(' ')}
    }
  }
`;

const TeamCapexOptionsQuery = gql`
  query TeamCapexOptions($id: ID!) {
    team(id: $id) {
      id
      capexResources {
        ${capexFields.join(' ')}
      }
    }
  }
`;

const FarmCapexOptionsQuery = gql`
  query FarmCapexOptions($farmId: ID!) {
    capexResources {
      ${capexFields.join(' ')}
    }
    farmTeamCapexTemplates(farmId: $farmId) {
      ${capexFields.join(' ')}
    }
    rawCapexes(farmId: $farmId) {
      ${capexFields.join(' ')}
      resourceId
    }
  }
`;

// a capex group can include an implement if the first capex exists and requires fuel
const canAddImplement = (capexes) =>
  capexes?.implement === undefined && capexes?.main?.requiresFuel;

const OperationCapexGroups = ({ className }) => {
  const { isTemplate } = useDataContext();
  const { teamId } = useParams();
  const { data } = useQueryWithFarmId(
    isTemplate
      ? teamId
        ? TeamCapexOptionsQuery
        : TemplateCapexOptionsQuery
      : FarmCapexOptionsQuery,
    {
      includeFarmId: !isTemplate,
      variables: { id: teamId },
      // Get latest data if team or public capex resource instantiated on a farm
      fetchPolicy: 'cache-and-network',
    },
  );

  const resourceIdMap = useMemo(
    () => _.keyBy(data?.rawCapexes || [], 'resourceId'),
    [data],
  );

  // A capex can be a "main" member of a capex group
  // if it either requires fuel and has a gallons per hour set (non legacy)
  // or if it does not require fueld and is not an implement
  const capexCanBeMainOption = (capex) =>
    (capex.requiresFuel && capex.gallonsFuelPerHour != null) ||
    (!capex.requiresFuel && capex.isImplement === false);

  const mainOptionGroups = useMemo(
    () => [
      {
        header: 'Farm Equipment',
        options: (data?.rawCapexes || []).filter((capex) =>
          capexCanBeMainOption(capex),
        ),
      },
      {
        header: 'Team Equipment',
        options: (data?.team?.capexResources || []).filter(
          (capex) => !resourceIdMap[capex.id] && capexCanBeMainOption(capex),
        ),
      },
      {
        header: 'Team Equipment',
        options: (data?.farmTeamCapexTemplates || []).filter(
          (capex) => !resourceIdMap[capex.id] && capexCanBeMainOption(capex),
        ),
      },
      {
        header: 'Public Equipment',
        options: (data?.capexResources || []).filter(
          (capex) => !resourceIdMap[capex.id] && capexCanBeMainOption(capex),
        ),
      },
    ],
    [data, resourceIdMap],
  );

  const implementOptionGroups = useMemo(
    () => [
      {
        header: 'Farm Equipment',
        options: (data?.rawCapexes || []).filter((capex) => capex.isImplement),
      },
      {
        header: 'Team Equipment',
        options: (data?.team?.capexResources || []).filter(
          (capex) => !resourceIdMap[capex.id] && capex.isImplement,
        ),
      },
      {
        header: 'Team Equipment',
        options: (data?.farmTeamCapexTemplates || []).filter(
          (capex) => !resourceIdMap[capex.id] && capex.isImplement,
        ),
      },
      {
        header: 'Public Equipment',
        options: (data?.capexResources || []).filter(
          (capex) => !resourceIdMap[capex.id] && capex.isImplement,
        ),
      },
    ],
    [data, resourceIdMap],
  );

  const { setFieldValue, touched, errors, values } = useFormikContext();
  const inputErrors = errors.capexGroups || [];
  const inputTouched = touched.capexGroups || [];
  const capexGroups = values.capexGroups || [];
  return (
    <ResourceSelectionFormGroup>
      <div>
        <div className={`d-flex ${className}`}>
          <FieldArray name="capexGroups">
            {({ push, remove }) => (
              <>
                {capexGroups.map(({ capexes, id, localId }, i) => (
                  <div className="mr-3 flex-shrink-0" key={id || localId}>
                    <ButtonGroup vertical>
                      <SelectorFormGroup
                        clearTextOnFocus
                        id={`capex-typeahead-${localId}`}
                        labelKey="name"
                        maxWidth="300px"
                        onChange={([v]) => {
                          setFieldValue(`capexGroups[${i}].capexes`, {
                            ...capexes,
                            main: v,
                          });
                        }}
                        optionGroups={mainOptionGroups}
                        optionsAreEqual={optionsAreEqualById}
                        placeholder={capexes.main?.name || 'Equipment'}
                        removeOption={() => remove(i)}
                        selectedOption={capexes.main}
                      />
                      {canAddImplement(capexes) && (
                        <Button
                          outline
                          onClick={() => {
                            setFieldValue(`capexGroups[${i}].capexes`, {
                              main: capexes.main,
                              implement: null,
                            });
                          }}
                        >
                          Add Implement
                        </Button>
                      )}
                      {capexes.implement !== undefined && (
                        <SelectorFormGroup
                          clearTextOnFocus
                          id={`implement-typeahead-${i}`}
                          labelKey="name"
                          maxWidth="300px"
                          onChange={([v]) => {
                            setFieldValue(`capexGroups[${i}].capexes`, {
                              ...capexes,
                              implement: v,
                            });
                          }}
                          optionGroups={implementOptionGroups}
                          optionsAreEqual={optionsAreEqualById}
                          placeholder={capexes?.implement?.name || 'Implement'}
                          removeOption={() =>
                            setFieldValue(`capexGroups[${i}].capexes`, {
                              ...capexes,
                              implement: undefined,
                            })
                          }
                          selectedOption={capexes?.implement}
                        />
                      )}
                    </ButtonGroup>
                    {inputTouched[i]?.capexes && inputErrors[i] && (
                      <FormFeedback className="d-block">
                        {typeof inputErrors === 'string' ? '' : inputErrors[i]}
                      </FormFeedback>
                    )}
                  </div>
                ))}
                <SquarePlusButton
                  onClick={() =>
                    push({
                      localId: nanoid(),
                      capexes: [],
                    })
                  }
                />
              </>
            )}
          </FieldArray>
        </div>
        {inputTouched?.length > 0 && inputErrors && (
          <FormFeedback className="d-block">
            {typeof inputErrors === 'string' ? inputErrors : ''}
          </FormFeedback>
        )}
      </div>
    </ResourceSelectionFormGroup>
  );
};

export default OperationCapexGroups;
